import { Note, Tag } from '../../../_service/data.service';
import { BulkCategoryResult, BulkStatusResult, BulkDeleteResult, BulkTagAddResult, BulkTagRemoveResult } from '../../../_service/data.service';

/** Sort options offered by the bulk "Export Notes..." dialog. */
export type ExportSortOrder =
  | 'default'
  | 'modified-desc'
  | 'modified-asc'
  | 'created-desc'
  | 'created-asc'
  | 'title-asc'
  | 'tags-asc'
  | 'status-asc';

export const EXPORT_SORT_ORDER_OPTIONS: { label: string; value: ExportSortOrder }[] = [
  { label: 'Default (as current view)', value: 'default' },
  { label: 'Modified Date (newest first)', value: 'modified-desc' },
  { label: 'Modified Date (oldest first)', value: 'modified-asc' },
  { label: 'Creation Date (newest first)', value: 'created-desc' },
  { label: 'Creation Date (oldest first)', value: 'created-asc' },
  { label: 'Title (alphabetical)', value: 'title-asc' },
  { label: 'Tags (alphabetical)', value: 'tags-asc' },
  { label: 'Status (alphabetical)', value: 'status-asc' },
];

/**
 * Pure bulk action logic extracted from HomeComponent.
 * Contains functions for applying bulk operations to the local note state.
 */
export class BulkActionsLogic {
  /**
   * Apply bulk category assignment to local state
   */
  static applyBulkCategoryLocally(
    allNotes: Note[],
    selectedNoteIds: string[],
    categoryId: string,
    updateTimestamp: boolean,
  ): Note[] {
    const now = new Date().toISOString();
    const updatedNoteIds = new Set(selectedNoteIds);

    return allNotes.map((note) => {
      if (!updatedNoteIds.has(note._id)) {
        return note;
      }
      const categories = Array.from(new Set([...(note.Categories || []), categoryId]));
      return {
        ...note,
        Categories: categories,
        last_modified: updateTimestamp ? now : note.last_modified,
      };
    });
  }

  /**
   * Apply bulk status update to local state
   */
  static applyBulkStatusLocally(
    allNotes: Note[],
    selectedNoteIds: string[],
    status: string,
    updateTimestamp: boolean,
  ): Note[] {
    const now = new Date().toISOString();
    const updatedNoteIds = new Set(selectedNoteIds);

    return allNotes.map((note) => {
      if (!updatedNoteIds.has(note._id)) {
        return note;
      }
      return {
        ...note,
        Status: status,
        last_modified: updateTimestamp ? now : note.last_modified,
      };
    });
  }

  /**
   * Add one or more tags to selected notes in local state.
   */
  static applyBulkTagsLocally(
    allNotes: Note[],
    selectedNoteIds: string[],
    tagIds: string[],
  ): Note[] {
    const updateSet = new Set(selectedNoteIds);
    const tagsToAdd = tagIds.filter((id) => !!id);

    return allNotes.map((note) => {
      if (!updateSet.has(note._id)) {
        return note;
      }

      const existingTagIds = (note.Tags || []).map((entry) => {
        if (typeof entry === 'string') {
          return entry;
        }
        return entry?._id || '';
      }).filter((id) => !!id);

      const mergedTags = Array.from(new Set([...existingTagIds, ...tagsToAdd]));
      return {
        ...note,
        Tags: mergedTags,
      };
    });
  }

  /**
   * Remove tags from selected notes in local state.
   * If removeAll is true, all tags are cleared from selected notes.
   */
  static applyBulkRemoveTagsLocally(
    allNotes: Note[],
    selectedNoteIds: string[],
    tagIds: string[],
    removeAll: boolean,
  ): Note[] {
    const updateSet = new Set(selectedNoteIds);
    const removeSet = new Set(tagIds.filter((id) => !!id));

    return allNotes.map((note) => {
      if (!updateSet.has(note._id)) {
        return note;
      }

      if (removeAll) {
        return {
          ...note,
          Tags: [],
        };
      }

      const remaining = (note.Tags || [])
        .map((entry) => {
          if (typeof entry === 'string') {
            return entry;
          }
          return entry?._id || '';
        })
        .filter((id) => !!id && !removeSet.has(id));

      return {
        ...note,
        Tags: Array.from(new Set(remaining)),
      };
    });
  }

  /**
   * Builds the base filename (no extension) for a bulk export: the user's
   * typed name (or a fallback default), with a `_YYYYMMDD-HHMMSS` timestamp
   * appended. Strips a user-typed extension first — the Format select is
   * authoritative for the extension, added separately by the caller via
   * NotePrintExportService.sanitizeFilenameForExtension().
   */
  static buildTimestampedExportBaseName(fileNameInput: string | undefined, now: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    const trimmed = (fileNameInput || '').trim();
    const base = trimmed ? trimmed.replace(/\.(pdf|docx|json)$/i, '') : 'notes_export';

    return `${base}_${timestamp}`;
  }

  /**
   * Order the selected note IDs for the bulk "Export Notes..." action.
   *
   * `currentViewSort` is the List view's persisted column-sort (sortBy/sortOrder,
   * e.g. read from ListComponent's LIST_SORT_STATE_KEY) — it's what "Default (as
   * current view)" replicates, since List is the only Home view with an explicit,
   * user-controlled sort order. The other options are direct, unambiguous sorts.
   */
  static sortNoteIdsForExport(
    selectedNoteIds: string[],
    allNotes: Note[],
    sortOrder: ExportSortOrder,
    tags: Tag[],
    currentViewSort: { sortBy: string; sortOrder: 'asc' | 'desc' },
  ): string[] {
    const byId = new Map(allNotes.map((note) => [note._id, note] as const));
    const notes = selectedNoteIds
      .map((id) => byId.get(id))
      .filter((note): note is Note => !!note);

    const tagNameById = new Map(tags.map((tag) => [tag._id, tag.name] as const));
    const tagNamesFor = (note: Note): string =>
      (note.Tags || [])
        .map((tag) => (typeof tag === 'string' ? tagNameById.get(tag) ?? tag : tag?.name ?? ''))
        .filter((name) => !!name)
        .join(', ')
        .toLowerCase();

    const compareStrings = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

    const comparator = BulkActionsLogic.buildExportComparator(sortOrder, tagNamesFor, compareStrings, currentViewSort);
    return [...notes].sort(comparator).map((note) => note._id);
  }

  private static buildExportComparator(
    sortOrder: ExportSortOrder,
    tagNamesFor: (note: Note) => string,
    compareStrings: (a: string, b: string) => number,
    currentViewSort: { sortBy: string; sortOrder: 'asc' | 'desc' },
  ): (a: Note, b: Note) => number {
    switch (sortOrder) {
      case 'modified-desc':
        return (a, b) => new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime();
      case 'modified-asc':
        return (a, b) => new Date(a.last_modified).getTime() - new Date(b.last_modified).getTime();
      case 'created-desc':
        return (a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime();
      case 'created-asc':
        return (a, b) => new Date(a.created_on).getTime() - new Date(b.created_on).getTime();
      case 'title-asc':
        return (a, b) => compareStrings(a.Title || '', b.Title || '');
      case 'tags-asc':
        return (a, b) => compareStrings(tagNamesFor(a), tagNamesFor(b));
      case 'status-asc':
        return (a, b) => compareStrings(a.Status || '', b.Status || '');
      case 'default':
      default:
        return BulkActionsLogic.buildListColumnComparator(currentViewSort, tagNamesFor, compareStrings);
    }
  }

  /** Replicates ListComponent's own column-sort comparator (see its
   * recomputeSortedNotes()) so "Default (as current view)" exports notes in
   * exactly the order the List view would currently show them in. */
  private static buildListColumnComparator(
    currentViewSort: { sortBy: string; sortOrder: 'asc' | 'desc' },
    tagNamesFor: (note: Note) => string,
    compareStrings: (a: string, b: string) => number,
  ): (a: Note, b: Note) => number {
    const { sortBy, sortOrder } = currentViewSort;
    const dir = sortOrder === 'desc' ? -1 : 1;

    if (!sortBy || sortBy === 'Link') {
      return () => 0; // No column sort active — keep selection order.
    }

    if (sortBy === 'Size') {
      return (a, b) => dir * ((a.Size ?? 0) - (b.Size ?? 0));
    }

    const getVal = (note: Note): string => {
      switch (sortBy) {
        case 'Tags':
          return tagNamesFor(note);
        case 'Title':
          return (note.Title || '').toLowerCase();
        case 'Modified':
          return note.last_modified || '';
        case 'Status':
          return (note.Status || '').toLowerCase();
        case 'Type':
          return (note.NoteType || '').toLowerCase();
        case 'ID':
          return (note._id || '').toLowerCase();
        default:
          return '';
      }
    };

    return (a, b) => dir * compareStrings(getVal(a), getVal(b));
  }

  /**
   * Remove notes from local state
   */
  static removeNotesLocally(allNotes: Note[], noteIds: string[]): Note[] {
    if (!noteIds?.length) {
      return allNotes;
    }
    const removalSet = new Set(noteIds);
    return allNotes.filter((note) => !removalSet.has(note._id));
  }

  /**
   * Extract the count of updated items from a bulk operation response.
   * Handles multiple response formats.
   */
  static getUpdatedCountFromBulk(
    data: BulkCategoryResult | BulkStatusResult | BulkDeleteResult | BulkTagAddResult | BulkTagRemoveResult | undefined,
    fallback: number,
  ): number {
    if (!data) {
      return fallback;
    }
    return (
      data.updatedCount ??
      data.updated ??
      (data as BulkDeleteResult).deletedCount ??
      (data as BulkDeleteResult).deleted ??
      data.count ??
      fallback
    );
  }
}
